import { GlobalFileHelper } from "@/constants/globalFile";
import { MAX_UPLOAD_SIZE_BYTES } from "@/constants/upload";
import { emptyFile } from "@/constants/zodTypes";
import { cn } from "@/lib/utils";
import { useFormField } from "@atoms/Form";
import { CloudUpload, Trash2 } from "lucide-react";
import { ReactNode } from "react";
import Dropzone, { ErrorCode } from "react-dropzone";
import FileLink from "./FileLink";
import { toast } from "sonner";

interface DropzoneProps {
  id?: string;
  children?: ReactNode;
  canBeRemoved?: boolean;
  accept?: { [key: string]: string[] };
}

const DropzoneMolecule = ({
  id,
  children,
  canBeRemoved = false,
  accept = {
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "application/pdf": [".pdf"],
  },
}: DropzoneProps) => {
  const { invalid, value, onChange } = useFormField();
  const valueIsEmpty = value instanceof File ? value.size === 0 : false;

  const errorCodesMessage = (code: ErrorCode) => {
    switch (code) {
      case ErrorCode.FileTooLarge:
        return `fichier trop volumineux`;
      case ErrorCode.FileInvalidType:
        return `format de fichier non autorisé (formats acceptés : ${Object.values(
          accept,
        )
          .flat()
          .join(", ")})`;
      case ErrorCode.FileTooSmall:
        return `fichier trop petit`;
      case ErrorCode.TooManyFiles:
        return `trop de fichiers sélectionnés`;
    }

    return "erreur inconnue";
  };

  return (
    <Dropzone
      onDrop={(acceptedFiles) => {
        if (acceptedFiles.length > 0) onChange(acceptedFiles[0]);
        else onChange(emptyFile());
      }}
      onDropRejected={(rejections) =>
        rejections.forEach(({ file, errors }) =>
          toast.error(
            `Impossible de charger le fichier ${file.name} : ${errors
              .map((error) => errorCodesMessage(error.code as ErrorCode))
              .join(" ; ")}`,
            { duration: 10000 },
          ),
        )
      }
      accept={accept}
      maxSize={MAX_UPLOAD_SIZE_BYTES}
      multiple={false}
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps()}
          className={cn(
            "flex px-6 py-4 flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white hover:bg-accent hover:text-accent-foreground cursor-pointer",
            invalid && "border-red-500",
          )}
        >
          <input {...getInputProps()} id={id} />
          <div className="p-2 flex items-center justify-center rounded-lg border border-gray-200 bg-white shadow-xs">
            <CloudUpload className="w-5 h-5" />
          </div>
          {children}
          <div className="flex flex-row gap-4">
            {/* File Name / Download Link */}
            {!valueIsEmpty ? (
              <FileLink file={value}>
                {GlobalFileHelper.fileName(value)}
              </FileLink>
            ) : (
              <span className="text-gray-500">Pas de fichier sélectionné</span>
            )}
            {/* Remove Button */}
            {canBeRemoved && !valueIsEmpty && (
              <button
                className="text-red-600 hover:text-red-800"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  onChange(emptyFile());
                }}
                aria-label="Remove file"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </Dropzone>
  );
};

export default DropzoneMolecule;
